import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { notifyClient } from "@/lib/notify";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Zap, MapPin, Calendar, Truck, Weight, ArrowLeft,
  Route, Package, FileText, Send, XCircle, CheckCircle2, AlertTriangle,
  Trophy, Phone, Mail, Clock, Settings2, ShieldCheck, Printer, X,
} from "lucide-react";
import { format } from "date-fns";
import { formatPhoneNumber } from "@/lib/formatPhone";
import { ShipperCreditAppReviewPanel } from "@/components/lr-direct/ShipperCreditAppReviewPanel";
import { ShipperPostAwardCard } from "@/components/lr-direct/ShipperPostAwardCard";
import { CreditAppFillPanel } from "@/components/lr-direct/CreditAppFillPanel";
import { useVerificationWarning } from "@/hooks/useVerificationWarning";
import { VerificationWarningModal } from "@/components/verification/VerificationWarningModal";
import confetti from "canvas-confetti";
import { toast as sonnerToast } from "sonner";
import { displayCompanyName } from "@/lib/displayCompanyName";
import { LtlBadge, isLtlLoad } from "@/components/lr-direct/LtlBadge";

// LR Direct is quote-only for now. Flip to true to re-enable the shipper Award flow.
const AWARDS_ENABLED = true;


/* ── types ── */
interface DirectLoad {
  id: string;
  load_number: string;
  origin_city: string;
  origin_state: string;
  origin_name: string;
  origin_address: string;
  destination_city: string;
  destination_state: string;
  destination_name: string;
  destination_address: string;
  equipment_type: string;
  pickup_date: string;
  delivery_date: string | null;
  commodity: string | null;
  weight_lbs: number | null;
  length_ft: number | null;
  total_miles: number | null;
  load_type: string | null;
  special_instructions: string | null;
  shipper_notes_to_brokers: string | null;
  bid_deadline: string | null;
  max_bidders: number;
  priority_tag: string;
  status: string;
  target_rate_min: number | null;
  target_rate_max: number | null;
  posted_at: string | null;
  created_by: string;
  awarded_bid_id: string | null;
  awarded_at: string | null;
  appointment_type: string | null;
  handling_requirements: string[] | null;
}

interface DirectStop {
  id: string;
  stop_number: number;
  stop_type: string;
  name: string;
  address: string;
  city: string;
  state: string;
  scheduled_date: string;
  scheduled_time_start: string | null;
  scheduled_time_end: string | null;
  instructions: string | null;
  pickup_number: string | null;
  pallet_count: number | null;
  confidential_notes: string | null;
  is_unlocked: boolean;
}

interface DirectBid {
  id: string;
  bid_amount: number;
  message: string | null;
  status: string;
  submitted_at: string;
  broker_id: string;
  broker_company_name?: string;
}

interface AwardContact {
  company_name: string;
  contact_name: string;
  email: string | null;
  phone: string | null;
}

/* ── component ── */
export default function LRDirectOpportunityPage() {
  usePageMeta({
    title: "LR Direct — Load Detail | LaneRunner",
    description: "View LR Direct load details.",
  });
  const { showWarningOrRun, modalProps } = useVerificationWarning();


  const { id } = useParams<{ id: string }>();
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [load, setLoad] = useState<DirectLoad | null>(null);
  const [stops, setStops] = useState<DirectStop[]>([]);
  const [myBid, setMyBid] = useState<DirectBid | null>(null);
  // Quote posts (post_type='quote') write to direct_quote_responses, NOT direct_bids.
  const [myQuote, setMyQuote] = useState<{ id: string; rate: number; capacity_note: string | null; status: string; created_at: string } | null>(null);
  const [allBids, setAllBids] = useState<DirectBid[]>([]);
  const [bidCount, setBidCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Bid form
  const [bidAmount, setBidAmount] = useState("");
  const [bidMessage, setBidMessage] = useState("");
  // UI-only LTL awareness speed bump (never persisted, never sent with the bid)
  const [ltlAcknowledged, setLtlAcknowledged] = useState(false);

  // Award flow
  const [awardingBidId, setAwardingBidId] = useState<string | null>(null);
  const [awardContact, setAwardContact] = useState<AwardContact | null>(null);
  const [awardContactLoaded, setAwardContactLoaded] = useState(false);
  const [showAwardBanner, setShowAwardBanner] = useState(false);
  const [awardedBrokerName, setAwardedBrokerName] = useState<string | null>(null);

  const isOwner = load?.created_by === user?.id;
  const userType = profile?.user_type;
  const BIDDER_ROLES = new Set(["broker", "broker_carrier", "carrier"]);
  const isBidder = !!userType && BIDDER_ROLES.has(userType);
  const isAwarded = load?.status === "awarded";
  const isQuotePost = (load as any)?.post_type === "quote";
  const isLtl = isLtlLoad(load?.load_type);

  useEffect(() => {
    if (!id) return;
    fetchOpportunity();
  }, [id]);

  const fetchOpportunity = async () => {
    setLoading(true);

    const { data: loadData, error: loadErr } = await supabase
      .from("direct_loads" as any)
      .select("*")
      .eq("id", id)
      .single();

    if (loadErr || !loadData) {
      setLoading(false);
      return;
    }
    const ld = loadData as unknown as DirectLoad;
    setLoad(ld);

    // Fetch stops via dispatch RPC (returns private fields when caller is unlocked)
    // + bids in parallel
    const stopsPromise = supabase
      .rpc("get_direct_load_dispatch" as any, { p_load_id: id });

    const isShipperOwner = user && ld.created_by === user.id;

    const isQuote = (ld as any).post_type === "quote";

    // Quote posts store broker responses in direct_quote_responses. Fetch the
    // caller's own active/awarded quote so the UI reflects it instead of
    // showing an empty "Place a Bid" form (which then fails with quote_exists).
    if (!isShipperOwner && user && isQuote) {
      const { data: qData } = await supabase
        .from("direct_quote_responses" as any)
        .select("id, rate, capacity_note, status, created_at")
        .eq("direct_load_id", id)
        .eq("broker_user_id", user.id)
        .in("status", ["active", "awarded"])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      setMyQuote((qData as any) ?? null);
    } else {
      setMyQuote(null);
    }

    const bidsPromise = isShipperOwner
      ? supabase
          .from("direct_bids" as any)
          .select("id, bid_amount, message, status, submitted_at, broker_id, broker_company_id")
          .eq("direct_load_id", id)
          .order("bid_amount", { ascending: true })
      : user
        ? supabase
            .from("direct_bids" as any)
            .select("id, bid_amount, message, status, submitted_at, broker_id")
            .eq("direct_load_id", id)
            .eq("broker_id", user.id)
            // Only ACTIVE rows may lock the bid form. Withdrawn / rejected /
            // lost / expired rows must not block a fresh bid — the server RPC
            // dedups on status='pending' only, so the UI must match.
            .in("status", ["pending", "accepted"])
            .order("submitted_at", { ascending: false })
            .limit(1)
        : null;

    const bidCountPromise = !isShipperOwner && user
      ? supabase
          .from("direct_bids" as any)
          .select("id", { count: "exact", head: true })
          .eq("direct_load_id", id)
          .eq("status", "pending")
      : null;

    const [stopsResult, bidsResult, bidCountResult] = await Promise.all([
      stopsPromise,
      bidsPromise,
      bidCountPromise,
    ]);

    if (stopsResult.data) setStops(stopsResult.data as unknown as DirectStop[]);

    if (isShipperOwner && bidsResult?.data) {
      const bidsData = bidsResult.data as any[];
      if (bidsData.length > 0) {
        const companyIds = [...new Set(bidsData.map((b: any) => b.broker_company_id).filter(Boolean))];
        let companyMap: Record<string, string> = {};
        if (companyIds.length > 0) {
          const { data: companies } = await supabase
            .from("companies")
            .select("id, name")
            .in("id", companyIds);
          if (companies) {
            companyMap = Object.fromEntries(companies.map((c: any) => [c.id, c.name]));
          }
        }
        const mapped: DirectBid[] = bidsData.map((b: any) => ({
          ...b,
          broker_company_name: displayCompanyName(companyMap[b.broker_company_id]) || "Unknown Broker",
        }));
        setAllBids(mapped);
        setBidCount(mapped.filter((b) => b.status === "pending").length);
      } else {
        setAllBids([]);
        setBidCount(0);
      }
    } else if (user && bidsResult?.data) {
      const bidData = bidsResult.data as any[];
      if (bidData.length > 0) {
        setMyBid((bidData as unknown as DirectBid[])[0]);
      }
      setBidCount(bidCountResult?.count ?? 0);

      if (ld.status === "awarded" && bidData.length > 0 && bidData[0].status === "accepted") {
        const { data: contactData } = await supabase.rpc("get_direct_award_contact", {
          p_load_id: id,
        } as any);
        if (contactData && (contactData as any[]).length > 0) {
          setAwardContact((contactData as any[])[0] as AwardContact);
        }
        setAwardContactLoaded(true);
      } else {
        setAwardContactLoaded(true);
      }
    }

    setLoading(false);
  };

  const handleSubmitBid = async () => {
    const amount = parseFloat(bidAmount.replace(/,/g, ""));
    if (!amount || amount <= 0) {
      toast({ variant: "destructive", title: "Enter a valid bid amount" });
      return;
    }
    setSubmitting(true);
    // Use the full 7-arg signature to match the current submit_direct_bid overload.
    const { data, error } = await supabase.rpc("submit_direct_bid", {
      p_direct_load_id: id,
      p_bid_amount: amount,
      p_message: bidMessage.trim() || null,
      p_ready_to_cover: false,
      p_truck_in_area: false,
      p_team_available: false,
      p_eta_hours: null,
    } as any);
    setSubmitting(false);
    const result = data as { success?: boolean; error?: string; code?: string } | null;
    if (error || !result?.success) {
      const code = result?.code;
      const friendly =
        code === "verification_required"
          ? "Your company must complete verification before bidding."
          : code === "lrd_subscription_required"
            ? "An LR Direct Private subscription is required to bid on this load."
            : code === "lrd_full_required"
              ? "An LR Direct Full subscription is required for open-market loads."
              : code === "not_in_network"
                ? "You're not in this shipper's private network yet."
                : result?.error || error?.message || "Something went wrong";
      toast({ variant: "destructive", title: "Bid failed", description: friendly });
      return;
    }
    const isQuoteResult = (result as any)?.is_quote === true;
    toast({
      title: isQuoteResult ? "Quote submitted" : "Bid submitted",
      description: `Your ${isQuoteResult ? "quote" : "bid"} of $${amount.toLocaleString()} has been submitted.`,
    });
    setBidAmount("");
    setBidMessage("");
    fetchOpportunity();
  };

  const handleWithdrawBid = async () => {
    if (!myBid) return;
    setSubmitting(true);
    const { data, error } = await supabase.rpc("withdraw_direct_bid", { p_bid_id: myBid.id, p_reason: null } as any);
    setSubmitting(false);
    const result = data as any;
    if (error || !result?.success) {
      toast({ variant: "destructive", title: "Withdraw failed", description: result?.error || error?.message });
      return;
    }
    toast({ title: "Bid withdrawn" });
    setMyBid(null);
    fetchOpportunity();
  };

  const handleAwardBid = async () => {
    if (!awardingBidId) return;
    const winningBid = allBids.find((b) => b.id === awardingBidId);
    const brokerName = winningBid?.broker_company_name ?? "the broker";
    setSubmitting(true);
    const { data, error } = await supabase.rpc("award_direct_bid", { p_bid_id: awardingBidId } as any);
    setSubmitting(false);
    setAwardingBidId(null);
    const result = data as any;
    if (error || !result?.success) {
      toast({ variant: "destructive", title: "Award failed", description: result?.error || error?.message || "Something went wrong" });
      return;
    }
    sonnerToast.success("Load awarded", {
      description: `Dispatch info revealed to ${brokerName}.`,
    });
    setAwardedBrokerName(brokerName);
    setShowAwardBanner(true);
    // Fire-and-forget broker award email + SMS via unified notify pipeline.
    try {
      const loadAny = load as any;
      const lane = loadAny ? `${loadAny.origin_city}, ${loadAny.origin_state} → ${loadAny.destination_city}, ${loadAny.destination_state}` : "your lane";
      if (winningBid?.broker_id) {
        notifyClient({
          recipientUserId: (winningBid as any).broker_id,
          eventType: "load_awarded",
          inAppType: "direct_load_awarded",
          title: `🎉 You Won the Bid!`,
          body: `${lane} — tap to start dispatch`,
          link: loadAny?.id ? `/lr-direct/dispatches/won/${loadAny.id}` : "/lr-direct/my-wins",
          loadId: loadAny?.id ?? null,
          metadata: { direct_load_id: loadAny?.id, direct_bid_id: awardingBidId },
          skipInApp: true,
          smsBody: `LR Direct: You won ${loadAny?.load_number ?? "a load"} (${lane}). Open dispatch.`,
          emailPayload: { lane, load_number: loadAny?.load_number },
        });
      }
      // Losing brokers — bid_not_awarded
      allBids.filter((b: any) => b.id !== awardingBidId && b.broker_id && b.status === "pending").forEach((b: any) => {
        notifyClient({
          recipientUserId: b.broker_id,
          eventType: "bid_not_awarded",
          inAppType: "direct_bid_not_awarded",
          title: `Bid closed on ${loadAny?.load_number ?? "load"}`,
          body: `The shipper selected a different broker for ${lane}. Keep an eye on the board for more loads.`,
          link: "/lr-direct/board",
          loadId: loadAny?.id ?? null,
          metadata: { direct_load_id: loadAny?.id, direct_bid_id: b.id },
          emailPayload: { lane, load_number: loadAny?.load_number },
        });
      });
    } catch (e) { console.warn("award notify failed", e); }

    // Celebratory confetti from the top center
    try {
      confetti({
        particleCount: 90,
        spread: 70,
        origin: { x: 0.5, y: 0.15 },
        colors: ["#02857E", "#10b981", "#fbbf24", "#ffffff"],
      });
    } catch { /* canvas-confetti is optional polish */ }
    fetchOpportunity();
  };


  const fmtEquipment = (type: string) =>
    type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const deadlinePassed = load?.bid_deadline ? new Date(load.bid_deadline) < new Date() : false;
  const slotsFull = load ? bidCount >= load.max_bidders : false;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!load) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-3">
        <AlertTriangle className="h-10 w-10 text-muted-foreground/40" />
        <p className="text-muted-foreground font-medium">Load not found</p>
        <Button variant="outline" onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    );
  }

  const hasRequirements = load.appointment_type || (load.handling_requirements && load.handling_requirements.length > 0);
  const hasNotes = load.special_instructions || load.shipper_notes_to_brokers;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card px-3 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => navigate(isOwner ? "/lr-direct/my-loads" : "/lr-direct/board")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-teal/10">
            <Zap className="h-4 w-4 text-brand-teal" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-base sm:text-lg font-bold text-foreground">Load Detail</h1>
              {isLtl && <LtlBadge size="lg" label="LTL / Partial" />}
              {load.priority_tag === "priority" && (
                <Badge variant="destructive" className="text-[10px] px-1.5 py-0">⚡ Priority</Badge>
              )}
              <Badge className={`text-[10px] uppercase font-bold border-0 ${
                load.status === "posted" ? "bg-emerald-500/15 text-emerald-500"
                  : load.status === "awarded" ? "bg-brand-teal/10 text-brand-teal"
                  : "bg-muted text-muted-foreground"
              }`}>
                {load.status === "posted" ? "Live" : load.status}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground font-mono truncate">{load.load_number}</p>
          </div>
          {isAwarded && (isOwner || myBid?.status === "accepted") && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 shrink-0"
              onClick={() => window.open(`/lr-direct/dispatch/${load.id}`, "_blank")}
            >
              <Printer className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Print Dispatch</span>
            </Button>
          )}
        </div>
      </div>

      {/* LTL emphasis banner (display only) */}
      {isLtl && (
        <div className="flex items-start gap-2.5 border-b border-amber-500 bg-amber-50 px-3 sm:px-6 py-2.5">
          <LtlBadge size="lg" label="LTL / Partial" className="mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-bold text-amber-900">This is an LTL / partial shipment</p>
            <p className="text-xs leading-snug text-amber-900/80">
              Not a full truckload move — quote LTL / partial pricing for this load.
            </p>
          </div>
        </div>
      )}

      {/* Award reveal banner */}
      {showAwardBanner && isAwarded && (
        <div className="border-b border-brand-teal/30 bg-brand-teal/10 px-3 sm:px-6 py-3 flex items-center gap-3">
          <Trophy className="h-5 w-5 text-brand-teal flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">
              Load awarded{awardedBrokerName ? ` to ${awardedBrokerName}` : ""} — dispatch info now visible.
            </p>
            <p className="text-xs text-muted-foreground">
              Pickup numbers, pallet counts, and confidential notes are revealed below.
            </p>
          </div>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            onClick={() => setShowAwardBanner(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Content */}
      <div className="px-3 sm:px-6 py-4 sm:py-5 grid gap-4 lg:grid-cols-3">
        {/* Main detail — 2 cols */}
        <div className="lg:col-span-2 space-y-4">
          {/* Lane Card */}
          <div className="border border-border rounded-lg bg-card p-4">
            <h2 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Lane</h2>
            <div className="flex items-center gap-2 text-sm sm:text-base font-semibold text-foreground flex-wrap">
              <MapPin className="h-4 w-4 text-brand-teal flex-shrink-0" />
              <span className="break-words">{load.origin_city}, {load.origin_state}</span>
              <span className="text-muted-foreground mx-1">→</span>
              <MapPin className="h-4 w-4 text-destructive flex-shrink-0" />
              <span className="break-words">{load.destination_city}, {load.destination_state}</span>
            </div>
            {load.total_miles && (
              <p className="text-sm text-muted-foreground mt-1.5 flex items-center gap-1">
                <Route className="h-3.5 w-3.5" /> {load.total_miles.toLocaleString()} miles
              </p>
            )}
          </div>

          {/* Timing Card */}
          <div className="border border-border rounded-lg bg-card p-4">
            <h2 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Timing</h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Pickup</p>
                <p className="font-medium text-foreground flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                  {format(new Date(load.pickup_date + "T00:00:00"), "MMM d, yyyy")}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">Delivery</p>
                <p className="font-medium text-foreground">
                  {load.delivery_date ? format(new Date(load.delivery_date + "T00:00:00"), "MMM d, yyyy") : "—"}
                </p>
              </div>
            </div>
          </div>

          {/* Equipment Card */}
          <div className="border border-border rounded-lg bg-card p-4">
            <h2 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Equipment & Cargo</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-3 gap-x-6 text-sm">
              <div>
                <p className="text-[10px] text-muted-foreground">Type</p>
                <p className="font-medium text-foreground flex items-center gap-1">
                  <Truck className="h-3.5 w-3.5 text-muted-foreground" />
                  {fmtEquipment(load.equipment_type)}
                </p>
              </div>
              {load.weight_lbs && (
                <div>
                  <p className="text-[10px] text-muted-foreground">Weight</p>
                  <p className="font-medium text-foreground flex items-center gap-1">
                    <Weight className="h-3.5 w-3.5 text-muted-foreground" />
                    {load.weight_lbs.toLocaleString()} lbs
                  </p>
                </div>
              )}
              {load.length_ft && (
                <div>
                  <p className="text-[10px] text-muted-foreground">Length</p>
                  <p className="font-medium text-foreground">{load.length_ft} ft</p>
                </div>
              )}
              {load.commodity && (
                <div>
                  <p className="text-[10px] text-muted-foreground">Commodity</p>
                  <p className="font-medium text-foreground flex items-center gap-1">
                    <Package className="h-3.5 w-3.5 text-muted-foreground" />
                    {load.commodity}
                  </p>
                </div>
              )}
              {load.load_type && (
                <div>
                  <p className="text-[10px] text-muted-foreground">Load Type</p>
                  <p className="font-medium text-foreground">{load.load_type}</p>
                </div>
              )}
            </div>
          </div>

          {/* Stops Card */}
          {stops.length > 0 && (
            <div className="border border-border rounded-lg bg-card p-4">
              <h2 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Stops</h2>
              <div className="space-y-3">
                {stops.map((stop) => {
                  const hasPrivate = stop.is_unlocked && (stop.pickup_number || stop.pallet_count != null || stop.confidential_notes);
                  return (
                  <div key={stop.id} className="flex items-start gap-3 text-sm">
                    <Badge variant="outline" className="text-[10px] w-16 justify-center flex-shrink-0 mt-0.5">
                      {stop.stop_type === "pickup" ? "Pick" : "Drop"} {stop.stop_number}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground">{stop.city}, {stop.state}</p>
                      {stop.name && <p className="text-xs text-muted-foreground">{stop.name}</p>}
                      {stop.address && <p className="text-xs text-muted-foreground">{stop.address}</p>}
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(stop.scheduled_date + "T00:00:00"), "MMM d, yyyy")}
                        {stop.scheduled_time_start && ` ${stop.scheduled_time_start}`}
                      </p>
                      {stop.instructions && (
                        <p className="text-xs text-muted-foreground/70 mt-0.5 italic">{stop.instructions}</p>
                      )}
                      {hasPrivate && (
                        <div className="mt-2 rounded-md border border-brand-teal/30 bg-brand-teal/5 px-2.5 py-2">
                          <p className="text-[10px] font-bold text-brand-teal uppercase tracking-wider flex items-center gap-1 mb-1.5">
                            <ShieldCheck className="h-3 w-3" />
                            Private Dispatch
                          </p>
                          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                            {stop.pickup_number && (
                              <div>
                                <span className="text-muted-foreground">{stop.stop_type === "pickup" ? "Pickup #" : "Delivery #"}: </span>
                                <span className="font-mono font-semibold text-foreground">{stop.pickup_number}</span>
                              </div>
                            )}
                            {stop.pallet_count != null && (
                              <div>
                                <span className="text-muted-foreground">Pallets: </span>
                                <span className="font-semibold text-foreground">{stop.pallet_count}</span>
                              </div>
                            )}
                          </div>
                          {stop.confidential_notes && (
                            <p className="text-xs text-foreground mt-1.5">{stop.confidential_notes}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Requirements Card */}
          {hasRequirements && (
            <div className="border border-border rounded-lg bg-card p-4">
              <h2 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Settings2 className="h-3.5 w-3.5" /> Requirements
              </h2>
              <div className="space-y-2">
                {load.appointment_type && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Scheduling:</span>
                    <Badge variant="outline" className="text-[10px]">
                      {load.appointment_type === "fcfs" ? "FCFS" : "Appointment"}
                    </Badge>
                  </div>
                )}
                {load.handling_requirements && load.handling_requirements.length > 0 && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1">Handling:</span>
                    <div className="flex flex-wrap gap-1">
                      {load.handling_requirements.map(req => (
                        <Badge key={req} variant="outline" className="text-[10px]">{req}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes Card */}
          {hasNotes && (
            <div className="border border-border rounded-lg bg-card p-4">
              <h2 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5" /> Notes & Instructions
              </h2>
              {load.shipper_notes_to_brokers && (
                <div className="mb-2">
                  <p className="text-[10px] font-semibold text-muted-foreground mb-1">Notes to Brokers</p>
                  <p className="text-sm text-foreground">{load.shipper_notes_to_brokers}</p>
                </div>
              )}
              {load.special_instructions && (
                <div>
                  <p className="text-[10px] font-semibold text-muted-foreground mb-1">Special Instructions</p>
                  <p className="text-sm text-foreground">{load.special_instructions}</p>
                </div>
              )}
            </div>
          )}

          {/* Owner: target rate */}
          {isOwner && (load.target_rate_min || load.target_rate_max) && (
            <div className="border border-border rounded-lg bg-card p-4">
              <h2 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Your Target Rate</h2>
              <p className="text-lg font-bold text-foreground tabular-nums">
                {load.target_rate_min && load.target_rate_max
                  ? `$${load.target_rate_min.toLocaleString()} – $${load.target_rate_max.toLocaleString()}`
                  : load.target_rate_max
                  ? `Up to $${load.target_rate_max.toLocaleString()}`
                  : `From $${load.target_rate_min!.toLocaleString()}`}
              </p>
              {load.total_miles && load.target_rate_max && (
                <p className="text-[10px] text-muted-foreground tabular-nums mt-0.5">
                  ${(load.target_rate_max / load.total_miles).toFixed(2)}/mi
                </p>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Bid status card */}
          <div className="border border-border rounded-lg bg-card p-4">
            <h2 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Bidding Status</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <Badge className={`text-[10px] uppercase font-bold border-0 ${
                  isAwarded ? "bg-brand-teal/10 text-brand-teal"
                    : load.status === "posted" ? "bg-emerald-500/15 text-emerald-500"
                    : "bg-muted text-muted-foreground"
                }`}>
                  {isAwarded ? "Awarded" : load.status === "posted" ? "Live" : load.status}
                </Badge>
              </div>
              {!isAwarded && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Slots</span>
                  <span className="font-medium text-foreground">{bidCount} / {load.max_bidders}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Deadline</span>
                <span className={`font-medium ${deadlinePassed ? "text-destructive" : "text-foreground"}`}>
                  {load.bid_deadline
                    ? format(new Date(load.bid_deadline), "MMM d, h:mm a")
                    : "Open"}
                </span>
              </div>
              {isAwarded && load.awarded_at && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Awarded</span>
                  <span className="font-medium text-foreground">
                    {format(new Date(load.awarded_at), "MMM d, h:mm a")}
                  </span>
                </div>
              )}
              {!isAwarded && deadlinePassed && (
                <p className="text-xs text-destructive flex items-center gap-1 mt-1">
                  <AlertTriangle className="h-3 w-3" /> Bid deadline has passed
                </p>
              )}
              {!isAwarded && slotsFull && !deadlinePassed && (
                <p className="text-xs text-warning flex items-center gap-1 mt-1">
                  <AlertTriangle className="h-3 w-3" /> All bid slots are filled
                </p>
              )}
            </div>
          </div>

          {/* ── SHIPPER: Bid Review ── */}
          {isOwner && (
            <div className="border border-border rounded-lg bg-card p-4">
              <h2 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-3">
                {isAwarded ? "Award Summary" : "Bids Received"}
              </h2>
              {allBids.length === 0 ? (
                <p className="text-sm text-muted-foreground">No bids received yet.</p>
              ) : (
                <div className="space-y-2">
                  {allBids
                    .sort((a, b) => {
                      if (a.status === "accepted") return -1;
                      if (b.status === "accepted") return 1;
                      return a.bid_amount - b.bid_amount;
                    })
                    .map((bid) => {
                      const isWinner = bid.status === "accepted";
                      const isRejected = bid.status === "rejected";
                      return (
                        <div
                          key={bid.id}
                          className={`border rounded-lg p-3 transition-all ${
                            isWinner
                              ? "border-brand-teal bg-brand-teal/5"
                              : isRejected
                              ? "border-border bg-muted/30 opacity-60"
                              : "border-border hover:border-primary/20"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-foreground">
                              {bid.broker_company_name || "Broker"}
                            </span>
                            {isWinner && (
                              <Badge className="bg-brand-teal/10 text-brand-teal text-[10px] uppercase font-bold border-0 gap-1">
                                <Trophy className="h-3 w-3" /> Winner
                              </Badge>
                            )}
                            {isRejected && (
                              <Badge className="bg-muted text-muted-foreground text-[10px] uppercase font-bold border-0">
                                Not Selected
                              </Badge>
                            )}
                            {bid.status === "pending" && (
                              <Badge className="bg-emerald-500/15 text-emerald-500 text-[10px] uppercase font-bold border-0">
                                Pending
                              </Badge>
                            )}
                            {bid.status === "withdrawn" && (
                              <Badge variant="outline" className="text-[10px] uppercase font-bold">
                                Withdrawn
                              </Badge>
                            )}
                          </div>
                          <p className="text-xl font-bold text-foreground tabular-nums">
                            ${bid.bid_amount.toLocaleString()}
                          </p>
                          {load.total_miles && (
                            <p className="text-[10px] text-muted-foreground tabular-nums">
                              ${(bid.bid_amount / load.total_miles).toFixed(2)}/mi
                            </p>
                          )}
                          {bid.message && (
                            <p className="text-xs text-muted-foreground mt-1 italic">"{bid.message}"</p>
                          )}
                          <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {format(new Date(bid.submitted_at), "MMM d, h:mm a")}
                          </p>
                          {AWARDS_ENABLED && !isAwarded && bid.status === "pending" && (
                            <Button
                              size="sm"
                              className="w-full mt-2 gap-1.5 text-xs"
                              style={{ backgroundColor: "hsl(var(--brand-teal))" }}
                              onClick={() => setAwardingBidId(bid.id)}
                              disabled={submitting}
                            >
                              <Trophy className="h-3.5 w-3.5" />
                              Award Broker
                            </Button>
                          )}

                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          )}

          {/* ── SHIPPER: Post-Award workspace (contact + doc stubs) ── */}
          {isOwner && isAwarded && (
            <ShipperPostAwardCard loadId={load.id} />
          )}

          {/* ── SHIPPER: Broker Credit App Review (awarded only) ── */}
          {isOwner && isAwarded && (
            <ShipperCreditAppReviewPanel loadId={load.id} />
          )}

          {/* ── BROKER: Post-Award Winner ── */}
          {!isOwner && isBidder && isAwarded && myBid?.status === "accepted" && (
            <div className="border-2 border-brand-teal rounded-lg bg-brand-teal/5 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="h-5 w-5 text-brand-teal" />
                <h2 className="text-[10px] font-bold text-brand-teal uppercase tracking-wider">
                  Awarded — You Won
                </h2>
              </div>
              <p className="text-sm text-foreground mb-3">
                You've been selected for this load. Contact the shipper to finalize details.
              </p>
              {awardContact ? (
                <div className="space-y-2 text-sm border-t border-brand-teal/20 pt-3">
                  <div>
                    <p className="text-[10px] text-muted-foreground">Company</p>
                    <p className="font-semibold text-foreground">{displayCompanyName(awardContact.company_name)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground">Contact</p>
                    <p className="font-medium text-foreground">{awardContact.contact_name}</p>
                  </div>
                  {awardContact.phone && (
                    <div>
                      <p className="text-[10px] text-muted-foreground">Phone</p>
                      <p className="font-medium text-foreground">{formatPhoneNumber(awardContact.phone)}</p>
                    </div>
                  )}
                  {awardContact.email && (
                    <div>
                      <p className="text-[10px] text-muted-foreground">Email</p>
                      <p className="font-medium text-foreground">{awardContact.email}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    {awardContact.phone && (
                      <Button size="sm" className="w-full gap-1.5 text-xs" style={{ backgroundColor: "hsl(var(--brand-teal))" }} asChild>
                        <a href={`tel:${awardContact.phone}`}>
                          <Phone className="h-3.5 w-3.5" /> Call
                        </a>
                      </Button>
                    )}
                    {awardContact.email && (
                      <Button size="sm" className="w-full gap-1.5 text-xs" style={{ backgroundColor: "hsl(var(--brand-teal))" }} asChild>
                        <a href={`mailto:${awardContact.email}`}>
                          <Mail className="h-3.5 w-3.5" /> Email
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              ) : !awardContactLoaded ? (
                <div className="text-center py-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-teal mx-auto mb-1" />
                  <p className="text-xs text-muted-foreground">Loading contact details...</p>
                </div>
              ) : (
                <div className="border-t border-brand-teal/20 pt-3 text-xs text-muted-foreground">
                  Contact info unavailable right now. Use Messages to reach the shipper.
                </div>
              )}
            </div>
          )}

          {/* ── BROKER: Dispatch Packet CTA (winner only) ── */}
          {!isOwner && isBidder && isAwarded && myBid?.status === "accepted" && (
            <div className="border-2 border-brand-teal/40 rounded-lg bg-card p-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-5 w-5 text-brand-teal" />
                <h2 className="text-[10px] font-bold text-brand-teal uppercase tracking-wider">
                  Dispatch Packet
                </h2>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Pickup numbers, gate codes, contact details, and stop instructions.
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  size="sm"
                  className="gap-1.5 text-xs"
                  style={{ backgroundColor: "hsl(var(--brand-teal))" }}
                  onClick={() => navigate(`/lr-direct/dispatches/won/${load.id}`)}
                >
                  <ShieldCheck className="h-3.5 w-3.5" /> Open Dispatch
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 text-xs"
                  onClick={() => window.open(`/lr-direct/dispatch/${load.id}`, "_blank")}
                >
                  <Printer className="h-3.5 w-3.5" /> Print / PDF
                </Button>
              </div>
            </div>
          )}

          {/* ── BROKER: Credit App Fill (winner only) ── */}
          {!isOwner && isBidder && isAwarded && myBid?.status === "accepted" && (
            <CreditAppFillPanel loadId={load.id} />
          )}

          {/* ── BROKER: Post-Award Loser ── */}
          {!isOwner && isBidder && isAwarded && myBid && myBid.status !== "accepted" && (
            <div className="border border-border rounded-lg bg-card p-4">
              <div className="flex items-center gap-2 mb-2">
                <XCircle className="h-5 w-5 text-muted-foreground" />
                <h2 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                  Not Selected
                </h2>
              </div>
              <p className="text-sm text-muted-foreground">
                This load has been awarded to another broker. Your bid of ${myBid.bid_amount.toLocaleString()} was not selected.
              </p>
            </div>
          )}

          {/* ── BROKER: Post-Award Non-Bidder ── */}
          {!isOwner && isBidder && isAwarded && !myBid && (
            <div className="border border-border rounded-lg bg-card p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-5 w-5 text-muted-foreground/40" />
                <h2 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                  Awarded
                </h2>
              </div>
              <p className="text-sm text-muted-foreground">
                This load has been awarded. No further bids are accepted.
              </p>
            </div>
          )}

          {/* ── BROKER: Active Bidding (not awarded) ── */}
          {!isOwner && isBidder && !isAwarded && (
            <>
              {isQuotePost && myQuote ? (
                <div className="border border-border rounded-lg bg-card p-4">
                  <h2 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Your Quote</h2>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Rate</span>
                      <span className="text-lg font-bold text-foreground tabular-nums">${Number(myQuote.rate).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Status</span>
                      <Badge className="bg-emerald-500/15 text-emerald-500 text-[10px] uppercase font-bold border-0">
                        {myQuote.status}
                      </Badge>
                    </div>
                    {myQuote.capacity_note && (
                      <div className="mt-2 pt-2 border-t border-border">
                        <p className="text-[10px] text-muted-foreground mb-1">Your Note</p>
                        <p className="text-sm text-foreground">{myQuote.capacity_note}</p>
                      </div>
                    )}
                    <p className="text-[10px] text-muted-foreground">
                      Submitted {format(new Date(myQuote.created_at), "MMM d, h:mm a")}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      This is an indicative quote post — the shipper reviews quotes before awarding.
                    </p>
                  </div>
                </div>
              ) : !isQuotePost && myBid && myBid.status === "pending" ? (
                <div className="border border-border rounded-lg bg-card p-4">
                  <h2 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-3">Your Bid</h2>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Amount</span>
                      <span className="text-lg font-bold text-foreground tabular-nums">${myBid.bid_amount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Status</span>
                      <Badge className="bg-emerald-500/15 text-emerald-500 text-[10px] uppercase font-bold border-0">
                        {myBid.status}
                      </Badge>
                    </div>
                    {myBid.message && (
                      <div className="mt-2 pt-2 border-t border-border">
                        <p className="text-[10px] text-muted-foreground mb-1">Your Message</p>
                        <p className="text-sm text-foreground">{myBid.message}</p>
                      </div>
                    )}
                    <p className="text-[10px] text-muted-foreground">
                      Submitted {format(new Date(myBid.submitted_at), "MMM d, h:mm a")}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-3 gap-1.5 text-destructive hover:text-destructive text-xs"
                    onClick={handleWithdrawBid}
                    disabled={submitting}
                  >
                    <XCircle className="h-3.5 w-3.5" />
                    Withdraw Bid
                  </Button>
                </div>
              ) : (
                <div className="border-2 border-brand-teal/30 rounded-lg bg-card p-4">
                  <h2 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-3">
                    {isQuotePost ? "Submit a Quote" : "Place a Bid"}
                  </h2>
                  {isLtl && (
                    <div className="mb-3 rounded-md border border-amber-500 bg-amber-50 p-2.5">
                      <div className="flex items-center gap-1.5">
                        <LtlBadge size="default" label="LTL / Partial" />
                        <p className="text-xs font-bold text-amber-900">
                          You are bidding on an LTL load
                        </p>
                      </div>
                      <p className="mt-1 text-[11px] leading-snug text-amber-900/80">
                        Submit LTL / partial pricing, not full truckload pricing.
                      </p>
                    </div>
                  )}
                  {deadlinePassed || (!isQuotePost && slotsFull) ? (
                    <p className="text-sm text-muted-foreground">
                      {deadlinePassed ? "Bidding is closed — deadline has passed." : "All bid slots are filled."}
                    </p>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="bid-amount" className="text-xs">{isQuotePost ? "Quote Rate ($) *" : "Bid Amount ($) *"}</Label>
                        <Input
                          id="bid-amount"
                          type="text"
                          inputMode="decimal"
                          placeholder="e.g. 2,500"
                          value={bidAmount}
                          onChange={(e) => setBidAmount(e.target.value)}
                          className="h-9 mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="bid-message" className="text-xs">Message (optional)</Label>
                        <Textarea
                          id="bid-message"
                          placeholder="Add a note to the shipper..."
                          value={bidMessage}
                          onChange={(e) => setBidMessage(e.target.value)}
                          className="mt-1 min-h-[60px]"
                          rows={2}
                        />
                      </div>
                      {isLtl && (
                        <label className="flex min-h-[44px] cursor-pointer items-center gap-2 rounded-md border border-amber-500 bg-amber-50 px-2.5 py-2">
                          <input
                            type="checkbox"
                            checked={ltlAcknowledged}
                            onChange={(e) => setLtlAcknowledged(e.target.checked)}
                            className="h-4 w-4 shrink-0 accent-amber-600"
                          />
                          <span className="text-[11px] font-semibold leading-snug text-amber-900">
                            Confirm you are submitting a quote for an LTL load.
                          </span>
                        </label>
                      )}
                      <Button
                        className="w-full gap-1.5 h-10 font-semibold"
                        style={{ backgroundColor: "hsl(var(--brand-teal))" }}
                        onClick={() => showWarningOrRun(handleSubmitBid)}
                        disabled={submitting || !bidAmount.trim() || (isLtl && !ltlAcknowledged)}
                      >
                        <Send className="h-3.5 w-3.5" />
                        {submitting ? "Submitting..." : isQuotePost ? "Submit Quote" : "Submit Bid"}
                      </Button>
                      {isLtl && !ltlAcknowledged && (
                        <p className="text-center text-[11px] font-semibold text-amber-700">
                          Check the LTL confirmation above to enable submit.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Award Confirmation Dialog */}
      <AlertDialog open={!!awardingBidId} onOpenChange={(open) => !open && setAwardingBidId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-brand-teal" />
              Award this broker?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Awarding this broker will immediately end bidding on this load.
              Your contact details will be shared with the winning broker so they can
              reach out to finalize. All other bids will be marked as not selected.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAwardBid}
              disabled={submitting}
              style={{ backgroundColor: "hsl(var(--brand-teal))" }}
            >
              {submitting ? "Awarding..." : "Confirm Award"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <VerificationWarningModal {...modalProps} />
    </div>
  );
}
